/**
 * Single row in the ConditionBuilder: `[variable] [op] [value]`.
 *
 * Type-driven UI: the available ops and the shape of the value input both
 * depend on the referenced variable's declared type.
 *   - number → equality (==, !=), inequalities (<, <=, >, >=); numeric input
 *   - boolean → equality only; true/false select
 *   - string → equality only; text input
 *
 * When the user changes which variable the row references, the value is
 * reset to the new variable's default (rather than coerced) — coercion
 * across types is rarely what authors mean.
 *
 * If the referenced variable was deleted (orphan effect), the row shows
 * a disabled error state. The validator will surface this as
 * `UNDECLARED_VARIABLE`; the editor just needs to render gracefully.
 */
import type { Comparison, ComparisonOp, VariableDecl } from '@branchworks/shared';

interface ComparisonRowProps {
  comparison: Comparison;
  variables: Record<string, VariableDecl>;
  onChange: (patch: Partial<Comparison>) => void;
}

export function ComparisonRow({ comparison, variables, onChange }: ComparisonRowProps) {
  const decl = variables[comparison.variable];
  const orphan = !decl;

  const handleVariableChange = (newVarName: string) => {
    const newDecl = variables[newVarName];
    if (!newDecl) return;
    // If switching to a type that doesn't support the current op, reset op
    // to equality. Number → string switch with `<` doesn't make sense.
    const newOp: ComparisonOp = supportsOp(newDecl.type, comparison.op)
      ? comparison.op
      : '==';
    onChange({ variable: newVarName, op: newOp, value: newDecl.default });
  };

  const variableNames = Object.keys(variables);
  const availableOps: ComparisonOp[] =
    decl?.type === 'number'
      ? ['==', '!=', '<', '<=', '>', '>=']
      : ['==', '!='];

  return (
    <div className="flex items-center gap-1">
      <select
        value={comparison.variable}
        onChange={(e) => handleVariableChange(e.target.value)}
        className="px-1.5 py-1 text-xs font-mono bg-white rounded border border-cream-border"
        style={{ maxWidth: 120 }}
        title={orphan ? 'This variable no longer exists' : (decl.description || comparison.variable)}
      >
        {orphan && <option value={comparison.variable}>{comparison.variable} (deleted)</option>}
        {variableNames.map((name) => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
      </select>
      <select
        value={comparison.op}
        onChange={(e) => onChange({ op: e.target.value as ComparisonOp })}
        disabled={orphan}
        className="px-1 py-1 text-xs bg-white rounded border border-cream-border"
      >
        {availableOps.map((op) => (
          <option key={op} value={op}>
            {op}
          </option>
        ))}
      </select>
      <div className="flex-1 min-w-0">{renderValueInput(comparison, decl, onChange)}</div>
    </div>
  );
}

function supportsOp(type: VariableDecl['type'], op: ComparisonOp): boolean {
  if (op === '==' || op === '!=') return true;
  return type === 'number';
}

function renderValueInput(
  comparison: Comparison,
  decl: VariableDecl | undefined,
  onChange: (patch: Partial<Comparison>) => void,
) {
  if (!decl) {
    return (
      <input
        disabled
        value=""
        placeholder="(deleted variable)"
        className="w-full px-2 py-1 text-xs rounded border border-terracotta-dark bg-white text-terracotta-dark"
      />
    );
  }
  if (decl.type === 'number') {
    return (
      <input
        type="number"
        value={Number(comparison.value)}
        onChange={(e) => {
          const n = Number(e.target.value);
          onChange({ value: Number.isFinite(n) ? n : 0 });
        }}
        className="w-full px-2 py-1 text-xs rounded border border-cream-border bg-white font-mono"
      />
    );
  }
  if (decl.type === 'boolean') {
    return (
      <select
        value={String(comparison.value)}
        onChange={(e) => onChange({ value: e.target.value === 'true' })}
        className="w-full px-2 py-1 text-xs rounded border border-cream-border bg-white"
      >
        <option value="false">false</option>
        <option value="true">true</option>
      </select>
    );
  }
  // string
  return (
    <input
      type="text"
      value={String(comparison.value ?? '')}
      onChange={(e) => onChange({ value: e.target.value })}
      className="w-full px-2 py-1 text-xs rounded border border-cream-border bg-white font-mono"
    />
  );
}
